import { Button, Icon, NotFound, TitleSegment } from '@britishcouncil/react-admin';
import {
  BreadcrumbsActionsPortal,
  DebouncedFullScreenLoader,
  MinWidthColumn,
  ModuleBadge,
  ReadonlyField,
  SessionStatusBadge,
} from 'components';
import { ROUTING_PATHS } from 'const';
import { generatePath, Link, Outlet, useLocation, useNavigate } from 'react-router';
import { SegmentGroup, Segment, Grid, GridColumn, Tab } from 'semantic-ui-react';
import { useLrwSessionDetails } from './useLrwSessionDetails';
import styled from '@emotion/styled';
import { LinkedSpeakingSessions } from './linked-speaking-sessions';
import { LrwSessionRooms } from './LrwSessionRooms';
import { useIntParam, useLrwSessionAllowedActions, useNavigationContext } from 'hooks';
import { ExamMaterials } from './ExamMaterials';
import { OnComputerWritingFormatBadges } from './OnComputerWritingFormatsBadges';
import { useEffect, useRef } from 'react';
import { SalesChannelAvailability } from './SalesChannelAvailability';
import { SessionDetailsRouteState, withActiveTabIndex } from '../../sessionDetailsRouteState';
import { SessionStatus } from 'api/planning';
import { ExamOverview } from './ExamOverview';
import { useLinkedSpeakingSessions } from './linked-speaking-sessions/useLinkedSpeakingSessions';
import { useLrwSessionExamOverview } from './useLrwSessionExamOverview';
import { allowedActionsStore } from 'store';
import { LrwSessionOptionsMenu } from './LrwSessionOptionsMenu';

const { lrwSessions } = ROUTING_PATHS;

export const LrwSessionDetails = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { locationId } = useNavigationContext();
  const previousLocationIdRef = useRef(locationId);
  const routeState = (location.state ?? {}) as SessionDetailsRouteState;
  const activeTabIndex = routeState.activeTabIndex ?? 0;
  const sessionId = useIntParam('sessionId');
  const { lrwSession, isLoading, isNotFound } = useLrwSessionDetails();
  const { canReleaseLrwSession, canUpdateLrwSession, canRemoveLrwSession } =
    useLrwSessionAllowedActions();
  const { releaseLrwSession, releaseLrwWithSpeakingSessions } = ROUTING_PATHS;
  const linkedSpeakingSessions = useLinkedSpeakingSessions();
  const hasLinkedSpeakingSessions =
    (linkedSpeakingSessions.linkedSpeakingSessions?.length ?? 0) > 0;
  const linkedSpeakingSessionId = linkedSpeakingSessions.linkedSpeakingSessions?.[0]?.id;

  const { data: linkedSpeakingSessionAllowedActions } =
    allowedActionsStore.service.useGetAllowedSpeakingSessionActionsQuery(
      { speakingSessionId: linkedSpeakingSessionId!, locationId },
      { skip: !linkedSpeakingSessionId }
    );
  const linkedSpeakingSessionCanBeReleased =
    linkedSpeakingSessionAllowedActions?.canReleaseSession ?? false;

  const shouldLoadExamOverview = hasLinkedSpeakingSessions;

  const { examOverview } = useLrwSessionExamOverview({
    enabled: shouldLoadExamOverview,
  });

  const hasExamOverviewWarning =
    shouldLoadExamOverview && (examOverview?.missingToCoverLrw ?? 0) > 0;

  useEffect(() => {
    if (
      previousLocationIdRef.current !== undefined &&
      locationId !== previousLocationIdRef.current
    ) {
      navigate(lrwSessions.absolutePath, { replace: true });
    }

    previousLocationIdRef.current = locationId;
  }, [locationId, navigate]);

  if (isNotFound) return <NotFound />;
  if (isLoading || !lrwSession) return <DebouncedFullScreenLoader />;

  return (
    <>
      <Outlet />
      {canReleaseLrwSession && (
        <BreadcrumbsActionsPortal>
          <S.ReleaseSessionButton
            to={generatePath(
              hasLinkedSpeakingSessions && linkedSpeakingSessionCanBeReleased
                ? releaseLrwWithSpeakingSessions.absolutePath
                : releaseLrwSession.absolutePath,
              { sessionId }
            )}
            state={location.state}
          >
            <Button compact primary color="green" data-testid="release-button">
              Release
            </Button>
          </S.ReleaseSessionButton>
        </BreadcrumbsActionsPortal>
      )}

      <S.SegmentGroup>
        <TitleSegment
          onReturn={() => navigate(lrwSessions.absolutePath)}
          title="LRW session details"
          leftSection={<SessionStatusBadge status={lrwSession.status} />}
          rightSection={
            <LrwSessionOptionsMenu
              canEdit={canUpdateLrwSession}
              canRemove={canRemoveLrwSession}
              sessionId={sessionId}
            />
          }
        />
        <Segment>
          <Grid>
            <Grid.Row>
              <MinWidthColumn>
                <ReadonlyField header="Date" data-testid="session-date">
                  <Icon name="calendar alternate outline" />
                  {lrwSession.date}
                </ReadonlyField>
              </MinWidthColumn>
              <MinWidthColumn>
                <ReadonlyField header="Start time" data-testid="session-start-time">
                  <Icon name="clock outline" />
                  {lrwSession.startTime.substring(0, 5)}
                </ReadonlyField>
              </MinWidthColumn>
              <MinWidthColumn>
                <ReadonlyField header="End time" data-testid="session-end-time">
                  <Icon name="clock outline" />
                  {lrwSession.endTime.substring(0, 5)}
                </ReadonlyField>
              </MinWidthColumn>
              <MinWidthColumn>
                <ReadonlyField header="Venue" data-testid="session-venue">
                  {lrwSession.venue}
                </ReadonlyField>
              </MinWidthColumn>
              <MinWidthColumn>
                <ReadonlyField
                  header="Test taker admission time"
                  data-testid="test-taker-admission-time"
                >
                  {lrwSession.testTakerAdmissionTimeMinutes} min
                </ReadonlyField>
              </MinWidthColumn>
              <S.GridColumn floated="right">
                <ModuleBadge />
                <S.SeparateLine />
                <OnComputerWritingFormatBadges
                  onComputerWritingFormats={lrwSession.onComputerWritingFormats}
                />
              </S.GridColumn>
            </Grid.Row>
          </Grid>
        </Segment>
      </S.SegmentGroup>

      <S.Tab
        activeIndex={activeTabIndex}
        onTabChange={(_, data) => {
          navigate(
            {
              pathname: location.pathname,
              search: location.search,
              hash: location.hash,
            },
            {
              replace: true,
              state: withActiveTabIndex(
                routeState,
                typeof data.activeIndex === 'number' ? data.activeIndex : 0
              ),
            }
          );
        }}
        panes={[
          { menuItem: 'Rooms', render: () => <LrwSessionRooms /> },
          {
            menuItem: 'Linked Speaking sessions',
            render: () => <LinkedSpeakingSessions />,
          },
          {
            menuItem: 'Exam materials',
            render: () => <ExamMaterials lrwSessionId={sessionId} />,
          },
          ...(lrwSession.status === SessionStatus.Released
            ? [
                {
                  menuItem: 'Sale channel availability',
                  render: () => <SalesChannelAvailability />,
                },
              ]
            : []),
          ...(hasLinkedSpeakingSessions
            ? [
                {
                  menuItem: {
                    content: (
                      <span>
                        {hasExamOverviewWarning && (
                          <Icon name="warning sign" color="orange" style={{ marginRight: 10 }} />
                        )}
                        Exam overview
                      </span>
                    ),
                  },
                  render: () => <ExamOverview />,
                },
              ]
            : []),
        ]}
      />
    </>
  );
};

const S = {
  SegmentGroup: styled(SegmentGroup)`
    &.ui.segments {
      margin: 1rem;
    }
  `,
  GridColumn: styled(GridColumn)`
    &.column {
      width: auto !important;
      min-width: auto;
    }
  `,
  Tab: styled(Tab)`
    & > .ui.tabular.menu {
      background: unset !important;
      border-top: none;
    }

    & > .ui.segment.active.tab {
      border-radius: unset;
    }
  `,
  SeparateLine: styled.div`
    margin: 0.5rem 0;
  `,
  ReleaseSessionButton: styled(Link)`
    margin-right: 1rem;
  `,
};